import type { ThemeMode } from '../types'

type WebviewEl = HTMLElement & {
  executeJavaScript?: (code: string, userGesture?: boolean) => Promise<unknown>
  insertCSS?: (css: string) => Promise<string>
  removeInsertedCSS?: (key: string) => Promise<void>
}

const cssKeys = new WeakMap<HTMLElement, string>()

export async function applyThemeToWebview(
  el: HTMLElement | null,
  theme: ThemeMode,
  tabId: string,
): Promise<void> {
  if (!el) return
  const webview = el as WebviewEl
  const light = theme === 'light'

  // Remove previous invert/css if any
  const prev = cssKeys.get(el)
  if (prev && webview.removeInsertedCSS) {
    try {
      await webview.removeInsertedCSS(prev)
    } catch {
      /* ignore */
    }
  }

  // Sites that respect prefers-color-scheme pick up Electron nativeTheme.
  // Extra: force color-scheme + a few known preference keys.
  const script = `
(() => {
  try {
    const light = ${light ? 'true' : 'false'};
    const root = document.documentElement;
    root.style.colorScheme = light ? 'light' : 'dark';
    root.setAttribute('data-color-mode', light ? 'light' : 'dark');
    root.setAttribute('data-theme', light ? 'light' : 'dark');
    root.classList.toggle('theme-light', light);
    root.classList.toggle('theme-dark', !light);
    root.classList.toggle('light-theme', light);
    root.classList.toggle('dark-theme', !light);
    try {
      localStorage.setItem('theme', light ? 'light' : 'dark');
      localStorage.setItem('color-theme', light ? 'light' : 'dark');
      localStorage.setItem('nightmode', light ? '0' : '1');
      localStorage.setItem('twilight.theme', light ? 'light' : 'dark');
    } catch (_) {}
    // Discord-ish
    try {
      localStorage.setItem('AccessibilityStore', JSON.stringify({
        ...(JSON.parse(localStorage.getItem('AccessibilityStore') || '{}')),
      }));
    } catch (_) {}
    // Prefer color scheme media override for stubborn CSS
    const id = 'luckys-place-theme-style';
    let style = document.getElementById(id);
    if (!style) {
      style = document.createElement('style');
      style.id = id;
      document.documentElement.appendChild(style);
    }
    style.textContent = ':root { color-scheme: ' + (light ? 'light' : 'dark') + ' !important; }';
    window.dispatchEvent(new Event('storage'));
  } catch (e) {
    console.warn('theme inject failed', e);
  }
})();
`

  if (webview.executeJavaScript) {
    try {
      await webview.executeJavaScript(script, false)
    } catch {
      /* guest may not be ready */
    }
  }

  // Visual fallback for always-dark UIs (X, Discord often ignore prefers-color-scheme)
  // Only invert in light mode so dark stays native.
  if (light && webview.insertCSS) {
    try {
      const key = await webview.insertCSS(`
        html.luckys-force-light, html {
          filter: invert(1) hue-rotate(180deg) contrast(0.95);
          background: #fff !important;
        }
        html.luckys-force-light img,
        html.luckys-force-light video,
        html.luckys-force-light picture,
        html.luckys-force-light canvas,
        html img,
        html video,
        html picture,
        html canvas,
        html [style*="background-image"] {
          filter: invert(1) hue-rotate(180deg);
        }
      `)
      cssKeys.set(el, key)
      if (webview.executeJavaScript) {
        await webview.executeJavaScript(
          `document.documentElement.classList.add('luckys-force-light');`,
          false,
        )
      }
    } catch {
      /* ignore */
    }
  } else if (webview.executeJavaScript) {
    try {
      await webview.executeJavaScript(
        `document.documentElement.classList.remove('luckys-force-light');`,
        false,
      )
    } catch {
      /* ignore */
    }
  }

  void tabId
}
